/**
 * Upload & Ingest Page - Async Event-Driven Upload with Real-Time Progress
 * 
 * Features:
 * - Drag-and-drop file upload
 * - Non-blocking async upload via event bus
 * - Real-time progress via WebSocket + polling fallback
 * - OCR text extraction preview
 * - Semantic embedding generation
 */
import React, { useState, useCallback, useRef } from 'react';
import { 
  Upload, 
  FileText, 
  Image, 
  File,
  CheckCircle2,
  AlertTriangle,
  X,
  Plus,
  Database,
  Eye,
  Sparkles,
  Search,
  FileSearch,
  Brain
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import { useUpload } from '../../hooks/useUpload';
import UploadProgress from '../../components/common/UploadProgress';
import { apiFetch } from '../../services/api_service';


const ACCEPTED_FILE_TYPES = [
  { ext: '.pdf', label: 'PDF', icon: FileText, ocrSupported: true },
  { ext: '.xlsx', label: 'Excel', icon: File, ocrSupported: false },
  { ext: '.csv', label: 'CSV', icon: Database, ocrSupported: false },
  { ext: '.png', label: 'PNG', icon: Image, ocrSupported: true },
  { ext: '.jpg', label: 'JPG', icon: Image, ocrSupported: true },
  { ext: '.jpeg', label: 'JPEG', icon: Image, ocrSupported: true },
  { ext: '.tiff', label: 'TIFF', icon: Image, ocrSupported: true },
  { ext: '.docx', label: 'Word', icon: FileText, ocrSupported: false },
  { ext: '.txt', label: 'TXT', icon: FileText, ocrSupported: false },
];

const OCR_SUPPORTED = ['.pdf', '.png', '.jpg', '.jpeg', '.tiff', '.tif', '.webp', '.bmp', '.gif'];

export default function UploadIngestPage() {
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [idpStatus, setIdpStatus] = useState(null);
  const { uploadFile, jobs, hasActiveJobs, clearCompleted } = useUpload();

  const getToken = () => localStorage.getItem('token');

  // Fetch IDP status on mount
  React.useEffect(() => {
    const fetchStatus = async () => {
      try {
        const data = await apiFetch(`/v1/idp/status`, {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        if (data && !data._error) {
          setIdpStatus(data);
        }
      } catch (e) {
        console.error('Failed to fetch IDP status:', e);
      }
    };
    fetchStatus();
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  }, []);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const handleFiles = async (files) => {
    for (const file of files) {
      try {
        const result = await uploadFile(file, {
          description: file.name,
        });

        if (result.isDuplicate) {
          toast({
            title: 'Duplicate File',
            description: `${file.name} already exists in the evidence library.`,
          });
        } else {
          toast({
            title: 'Upload Complete',
            description: `${file.name} was added to the evidence library.`,
          });
        }
      } catch (err) {
        toast({
          title: 'Upload Failed',
          description: err.message || `Failed to upload ${file.name}`,
          variant: 'destructive',
        });
      }
    }
  };

  return (
    <div className="space-y-6" data-testid="upload-ingest-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Upload & Ingest</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            Add evidence with AI-powered text extraction and semantic indexing
          </p>
        </div>
        <div className="flex items-center gap-3">
          {idpStatus && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-[#1A1A1A] border border-[#2F2F2F] rounded-lg">
              <div className={`w-2 h-2 rounded-full ${idpStatus.ocr?.available ? 'bg-emerald-400' : 'bg-red-400'}`} />
              <span className="text-xs text-[#A0A0A0]">OCR</span>
              <div className={`w-2 h-2 rounded-full ${idpStatus.embeddings?.available ? 'bg-emerald-400' : 'bg-red-400'}`} />
              <span className="text-xs text-[#A0A0A0]">AI</span>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Upload Zone & Progress */}
        <div className="space-y-4">
          {/* Upload Zone */}
          <div
            className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all ${
              isDragging 
                ? 'border-emerald-500 bg-emerald-500/10' 
                : 'border-[#2F2F2F] hover:border-[#4B4B4B] bg-[#0D0D0D]'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            data-testid="upload-dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileSelect}
              accept={ACCEPTED_FILE_TYPES.map(t => t.ext).join(',')}
              data-testid="file-input"
            />
            
            <div className="flex flex-col items-center">
              <div className={`p-4 rounded-full mb-4 ${
                isDragging ? 'bg-emerald-500/20' : 'bg-[#1A1A1A]'
              }`}>
                <Upload className={`w-8 h-8 ${
                  isDragging ? 'text-emerald-400' : 'text-[#6B6B6B]'
                }`} />
              </div>
              
              <h3 className="text-lg font-medium text-white mb-2">
                {isDragging ? 'Drop files here' : 'Drag & drop files'}
              </h3>
              <p className="text-sm text-[#6B6B6B] mb-4">
                Files are uploaded instantly and processed in the background
              </p>
              
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                className="border-[#2F2F2F]"
                data-testid="select-files-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Select Files
              </Button>
              
              <div className="flex flex-wrap justify-center gap-2 mt-4">
                {ACCEPTED_FILE_TYPES.map(type => (
                  <div 
                    key={type.ext}
                    className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                      type.ocrSupported 
                        ? 'bg-purple-500/20 text-purple-400' 
                        : 'bg-[#1F1F1F] text-[#6B6B6B]'
                    }`}
                    title={type.ocrSupported ? 'OCR supported' : 'No OCR'}
                  >
                    <type.icon className="w-3 h-3" />
                    {type.label}
                    {type.ocrSupported && <FileSearch className="w-3 h-3" />}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Real-Time Upload Progress */}
          <UploadProgress jobs={jobs} onClear={clearCompleted} />
        </div>

        {/* Right Column: Info & Stats */}
        <div className="space-y-4">
          {/* How It Works */}
          <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl overflow-hidden">
            <div className="p-4 border-b border-[#1F1F1F]">
              <h3 className="font-medium text-white flex items-center gap-2">
                <Eye className="w-4 h-4 text-[#6B6B6B]" />
                How It Works
              </h3>
            </div>
            <div className="p-4 space-y-3">
              <Step number={1} title="Upload" description="Drop files or click to browse. Accepted: PDF, Excel, CSV, images, Word docs." />
              <Step number={2} title="Extract" description="OCR automatically extracts text from scanned documents and images." />
              <Step number={3} title="Index" description="AI generates semantic embeddings for intelligent search across all evidence." />
              <Step number={4} title="Ready" description="Evidence is stored and available in the Evidence Library for framework mapping." />
            </div>
          </div>

          {/* Recent Jobs Summary */}
          {jobs.length > 0 && (
            <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
              <h4 className="text-sm font-medium text-white mb-3">Session Summary</h4>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-2 bg-[#0D0D0D] rounded-lg">
                  <p className="text-lg font-bold text-white" data-testid="total-jobs-count">{jobs.length}</p>
                  <p className="text-xs text-[#6B6B6B]">Total</p>
                </div>
                <div className="p-2 bg-[#0D0D0D] rounded-lg">
                  <p className="text-lg font-bold text-emerald-400" data-testid="completed-jobs-count">
                    {jobs.filter(j => j.status === 'completed').length}
                  </p>
                  <p className="text-xs text-[#6B6B6B]">Completed</p>
                </div>
                <div className="p-2 bg-[#0D0D0D] rounded-lg">
                  <p className="text-lg font-bold text-blue-400" data-testid="active-jobs-count">
                    {jobs.filter(j => j.status !== 'completed' && j.status !== 'failed').length}
                  </p>
                  <p className="text-xs text-[#6B6B6B]">Active</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* IDP Features Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 bg-[#111111] border border-[#1F1F1F] rounded-xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <FileSearch className="w-5 h-5 text-purple-400" />
            </div>
            <h4 className="font-medium text-white">OCR Extraction</h4>
          </div>
          <p className="text-sm text-[#6B6B6B]">
            Extract text from scanned documents, PDFs, and images using Tesseract OCR
          </p>
        </div>
        
        <div className="p-4 bg-[#111111] border border-[#1F1F1F] rounded-xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-emerald-500/20 rounded-lg">
              <Brain className="w-5 h-5 text-emerald-400" />
            </div>
            <h4 className="font-medium text-white">Semantic Indexing</h4>
          </div>
          <p className="text-sm text-[#6B6B6B]">
            Generate vector embeddings for AI-powered semantic search across your evidence
          </p>
        </div>
        
        <div className="p-4 bg-[#111111] border border-[#1F1F1F] rounded-xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Search className="w-5 h-5 text-blue-400" />
            </div>
            <h4 className="font-medium text-white">Find Similar</h4>
          </div>
          <p className="text-sm text-[#6B6B6B]">
            Discover related evidence across frameworks to maximize reuse
          </p>
        </div>
      </div>
    </div>
  );
}

function Step({ number, title, description }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
        {number}
      </div>
      <div>
        <p className="text-sm font-medium text-white">{title}</p>
        <p className="text-xs text-[#6B6B6B]">{description}</p>
      </div>
    </div>
  );
}
